import { cn } from '@/lib/utils';
import useCountUp from '../../hooks/useCountUp';

/**
 * Hochzaehlende Kennzahl, die ihre Breite nicht veraendert.
 *
 * Zwei Effekte wuerden sonst das Layout waehrend der Animation verschieben:
 *   1. unterschiedlich breite Ziffern (proportionale Schrift) -> `tabular-nums`
 *   2. unterschiedlich viele Stellen (0 -> 39) -> unsichtbarer Platzhalter
 *      mit dem Endwert reserviert die endgueltige Breite, der laufende Wert
 *      liegt absolut darueber.
 */
type CountUpNumberProps = {
  /** Zielwert, auf den hochgezaehlt wird. */
  end: number;
  /** Startet die Animation erst, wenn die Daten da sind. */
  active?: boolean;
  /** Erhoehen, um dieselbe Zahl erneut hochzaehlen zu lassen (Refresh). */
  restartKey?: number;
  duration?: number;
  decimalPlaces?: number;
  /** Ausrichtung innerhalb der reservierten Breite. */
  align?: 'left' | 'right';
  className?: string;
};

export default function CountUpNumber({
  end,
  active = true,
  restartKey = 0,
  duration = 2000,
  decimalPlaces = 0,
  align = 'left',
  className,
}: CountUpNumberProps) {
  const display = useCountUp({
    end,
    duration,
    startOnMount: active,
    decimalPlaces,
    restartKey,
  });

  // Der Endwert ist immer der breiteste Zustand – er gibt die Box vor.
  const widest = end.toFixed(decimalPlaces);

  return (
    <span className={cn('relative inline-block tabular-nums', className)}>
      <span aria-hidden="true" className="invisible">
        {widest}
      </span>
      <span
        data-testid="countup-value"
        className={cn('absolute inset-0', align === 'right' ? 'text-right' : 'text-left')}
      >
        {display}
      </span>
    </span>
  );
}
